import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Header from "@/components/Header";
import ZohoChatWidget from "@/components/ZohoChatWidget";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import ProfileNew from "@/pages/ProfileNew";
import ProfileDetail from "@/pages/ProfileDetail";
import CalibrationDetail from "@/pages/CalibrationDetail";
import CalibrationRate from "@/pages/CalibrationRate";
import CalibrationComparison from "@/pages/CalibrationComparison";
import EmployeePortal from "@/pages/EmployeePortal";
import EmployeeComparison from "@/pages/EmployeeComparison";
import Nominate360 from "@/pages/Nominate360";
import Approve360 from "@/pages/Approve360";
import Rate360 from "@/pages/Rate360";
import Results360 from "@/pages/Results360";
import EmployeeSummary360 from "@/pages/EmployeeSummary360";
import OrgAdmin from "@/pages/OrgAdmin";
import PlatformAdmin from "@/pages/PlatformAdmin";
import PlatformPlans from "@/pages/PlatformPlans";
import PlatformOrgs from "@/pages/PlatformOrgs";
import PlatformUsers from "@/pages/PlatformUsers";
import PDGenerator from "@/pages/PDGenerator";
import VerifyEmail from "@/pages/VerifyEmail";
import Terms from "@/pages/Terms";
import Pricing from "@/pages/Pricing";
import FAQ from "@/pages/FAQ";
import Blog from "@/pages/Blog";
import Welcome from "@/pages/Welcome";
import "@/App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 font-mono-cal text-xs">[AUTH CHECK…]</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Router v7 defers navigate() in a transition, so the guard must route fresh signups itself.
function PostSignupRedirect() {
  if (sessionStorage.getItem("calibrate_signup_welcome")) {
    return <Navigate to="/welcome" replace state={{ fromSignup: true }} />;
  }
  return <Navigate to="/dashboard" replace />;
}

function Shell() {
  const { user } = useAuth();
  const loc = useLocation();
  const isPublic = loc.pathname.startsWith("/e/");

  return (
    <>
      {!isPublic && <Header />}
      <ZohoChatWidget />
      <Routes>
        <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <Landing />} />
        <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
        <Route path="/register" element={user ? <PostSignupRedirect /> : <Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/welcome" element={<Protected><Welcome /></Protected>} />
        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/profiles/new" element={<Protected><ProfileNew /></Protected>} />
        <Route path="/profiles/:id" element={<Protected><ProfileDetail /></Protected>} />
        <Route path="/calibrations/:id" element={<Protected><CalibrationDetail /></Protected>} />
        <Route path="/calibrations/:id/rate" element={<Protected><CalibrationRate /></Protected>} />
        <Route path="/calibrations/:id/comparison" element={<Protected><CalibrationComparison /></Protected>} />
        <Route path="/admin/org" element={<Protected><OrgAdmin /></Protected>} />
        <Route path="/admin/platform" element={<Protected><PlatformAdmin /></Protected>} />
        <Route path="/admin/platform/plans" element={<Protected><PlatformPlans /></Protected>} />
        <Route path="/admin/platform/orgs" element={<Protected><PlatformOrgs /></Protected>} />
        <Route path="/admin/platform/users" element={<Protected><PlatformUsers /></Protected>} />
        <Route path="/pd-generator" element={<Protected><PDGenerator /></Protected>} />
        {/* Public magic-link routes */}
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/e/:token" element={<EmployeePortal />} />
        <Route path="/e/:token/done" element={<EmployeeComparison />} />
        <Route path="/360/nominate/:token" element={<Nominate360 />} />
        <Route path="/360/approve/:cycleId" element={<Protected><Approve360 /></Protected>} />
        <Route path="/360/rate/:token" element={<Rate360 />} />
        <Route path="/360/employee-summary/:token" element={<EmployeeSummary360 />} />
        <Route path="/360/results/:cycleId" element={<Protected><Results360 /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster position="bottom-right" richColors closeButton expand={true} />
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Shell />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
